import React, { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  Select,
} from "@mui/material";
import DisplayDate from "../../../../../../Component/DisplayDate/DisplayDate";
import styles from "./../ListItem.module.css";
import Button from "../../../../../../Component/Button/Button";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  ProductName,
  translateProductName,
} from "../../../../../../Component/Product/ProductName";
import {
  Await,
  useNavigate,
  useParams,
  useRouteLoaderData,
} from "react-router";
import useShowTrees from "../../_field_id/useShowTrees";
import { useTreesLastModified } from "../../_field_id/useTreesLastModified";
import { DrivingDay } from "../../../../../../API/drivingDay";
import LayerListItem from "./LayerListItem";
import PhotoSelect from "./PhotoSelect";
import ExportToAgroBtn from "./ExportToAgroBtn";
import { useIsAllowedAgromanagerUser } from "../../../../../../Component/AuthGuard/useIsAllowedAgromanagerUser";
import { ResultSet } from "../../../../../../API/fields";
import MessageFlash, {
  MessageLevel,
} from "../../../../../../Component/message-flash/MessageFlash";
import { filterResultSetsByLastCalibrated } from "./filter-last-calibrated";
import { hasDisplacedCalibration } from "./has-displaced-calibration";
import { isSingleDayDrive } from "./match-driving-day";
import {
  useCalibrationDiscovery,
  useLatestCalibratedSelection,
  useRevalidateOnCalibrationSuccess,
} from "./useCalibrationTracking";

export enum Products {
  BLOSSOM = "OR2",
  VIGOUR = "OR1",
  TS_BLOSSOM = "TS-BLOSSOM",
  TS_APPLE = "TS-APPLE",
  TS_VIGOUR = "TS-VIGOUR",
}

export type ResultSetSelectProps = {
  resultSetList: ResultSet[];
  filter?: string | null;
  selectedResultSetId?: string;
  onSelect: (
    selectedResultSetId: string | null,
    options?: { replace: boolean },
  ) => void;
  selectedResultType?: Products | null;
  onDownloadResult: (selectedResultSetId: string) => void;
  onCreateTaskMap: (selectedResultSetId: string) => void;
  tilingSelector: React.ReactNode;
};

function isBlossomProduct(sku: string | null | undefined): boolean {
  const blossomProducts: unknown[] = [Products.BLOSSOM, Products.TS_BLOSSOM];
  return blossomProducts.includes(sku);
}

function isAppleProduct(sku: string | null | undefined): boolean {
  const appleProducts: unknown[] = [Products.TS_APPLE];
  return appleProducts.includes(sku);
}

function isVigourProduct(sku: string | null | undefined): boolean {
  const vigourProducts: unknown[] = [Products.VIGOUR, Products.TS_VIGOUR];
  return vigourProducts.includes(sku);
}

function sortResultSets(resultSets: ResultSet[]) {
  const result = [...resultSets];
  let groupStart = 0;

  for (let i = 1; i <= result.length; i++) {
    if (
      i === result.length ||
      result[i].detection_dates[0] !== result[groupStart].detection_dates[0]
    ) {
      if (i - groupStart > 1) {
        result
          .slice(groupStart, i)
          .reverse()
          .forEach((item, idx) => {
            result[groupStart + idx] = item;
          });
      }
      groupStart = i;
    }
  }
  return result;
}
export function ResultSetSelect({
  resultSetList,
  selectedResultSetId,
  onSelect,
  onDownloadResult,
  onCreateTaskMap,
  selectedResultType,
  tilingSelector,
}: ResultSetSelectProps) {
  const { t, i18n } = useTranslation();
  const [showTrees, setShowTrees] = useShowTrees();
  const treesLastModified = useTreesLastModified((state) => state.lastModified);
  const { drivingDays } = useRouteLoaderData("fieldId") as {
    drivingDays: DrivingDay[];
  };

  const [openedResultType, setOpenResultType] = useState<Products | null>(null);
  const foundResult = resultSetList.find(
    (resultSet) => resultSet.id === selectedResultSetId,
  );

  const resultType = foundResult
    ? foundResult.product_sku
    : openedResultType || selectedResultType;

  const appleResults = sortResultSets(
    filterResultSetsByLastCalibrated(
      resultSetList.filter((resultSet) =>
        isAppleProduct(resultSet.product_sku),
      ),
    ),
  );

  const blossomResults = sortResultSets(
    resultSetList.filter((resultSet) =>
      isBlossomProduct(resultSet.product_sku),
    ),
  );
  const vigourResults = sortResultSets(
    resultSetList.filter((resultSet) => isVigourProduct(resultSet.product_sku)),
  );

  useCalibrationDiscovery(appleResults);
  useRevalidateOnCalibrationSuccess(resultSetList);
  useLatestCalibratedSelection(
    foundResult && isAppleProduct(foundResult.product_sku)
      ? foundResult
      : undefined,
    appleResults,
    onSelect,
  );

  return (
    <List className={styles.list}>
      <ListSubheader component="li" className={styles.listSubheader}>
        {t("heading.Results")}
      </ListSubheader>
      <ListItem key="no" className={styles.listItem}>
        <ListItemButton
          onClick={() => {
            onSelect(null);
            setOpenResultType(null);
          }}
        >
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Radio edge="start" disableRipple checked={!resultType} />
          </ListItemIcon>
          <ListItemText primary={t("No results")} />
        </ListItemButton>
      </ListItem>
      <Suspense>
        <Await resolve={drivingDays}>
          {(drivingDays: DrivingDay[]) => (
            <>
              {appleResults.length > 0 && (
                <LayerListItem
                  onActivate={() => {
                    onSelect(appleResults.at(0)?.id || null);
                    setOpenResultType(Products.TS_APPLE);
                  }}
                  label={<ProductName sku={Products.TS_APPLE} />}
                  active={isAppleProduct(resultType)}
                  widget="radio"
                >
                  <ProductResults
                    resultSets={appleResults}
                    selectedResultSetId={selectedResultSetId}
                    product={Products.TS_APPLE}
                    drivingDays={drivingDays}
                    onDownloadResult={() =>
                      selectedResultSetId &&
                      onDownloadResult(selectedResultSetId)
                    }
                    onCreateTaskMap={() =>
                      selectedResultSetId &&
                      onCreateTaskMap(selectedResultSetId)
                    }
                    onSelect={onSelect}
                  />
                </LayerListItem>
              )}

              <LayerListItem
                onActivate={() => {
                  onSelect(blossomResults.at(0)?.id || null);
                  setOpenResultType(Products.BLOSSOM);
                }}
                label={<ProductName sku={Products.BLOSSOM} />}
                active={isBlossomProduct(resultType)}
                widget="radio"
              >
                <ProductResults
                  resultSets={blossomResults}
                  selectedResultSetId={selectedResultSetId}
                  product={Products.BLOSSOM}
                  onDownloadResult={() =>
                    selectedResultSetId && onDownloadResult(selectedResultSetId)
                  }
                  onCreateTaskMap={() =>
                    selectedResultSetId && onCreateTaskMap(selectedResultSetId)
                  }
                  onSelect={onSelect}
                />
              </LayerListItem>

              <LayerListItem
                onActivate={() => {
                  onSelect(vigourResults.at(0)?.id || null);
                  setOpenResultType(Products.VIGOUR);
                }}
                label={<ProductName sku={Products.VIGOUR} />}
                active={isVigourProduct(resultType)}
                widget="radio"
              >
                <ProductResults
                  resultSets={vigourResults}
                  selectedResultSetId={selectedResultSetId}
                  product={Products.VIGOUR}
                  drivingDays={drivingDays}
                  onDownloadResult={() =>
                    selectedResultSetId && onDownloadResult(selectedResultSetId)
                  }
                  onCreateTaskMap={() =>
                    selectedResultSetId && onCreateTaskMap(selectedResultSetId)
                  }
                  onSelect={onSelect}
                />
              </LayerListItem>
            </>
          )}
        </Await>
      </Suspense>
      <ListSubheader component="li" className={styles.listSubheader}>
        {t("heading.Layers")}
      </ListSubheader>
      {tilingSelector}
      <LayerListItem
        label={
          <>
            <span className={styles.layerItemTitle}>{t("Trees")}</span>
            {treesLastModified && (
              <span className={styles.layerItemDate}>
                {`${t("label.updated_at")} ${DateTime.fromHTTP(
                  treesLastModified,
                )
                  .setLocale(i18n.language)
                  .toLocaleString(DateTime.DATE_FULL)}`}
              </span>
            )}
          </>
        }
        active={showTrees}
        onActivate={() => setShowTrees(!showTrees)}
      />
      <Suspense>
        <Await resolve={drivingDays}>
          {(drivingDays) => (
            <PhotoSelect
              drivingDays={drivingDays}
              drivingDayId={foundResult?.driving_day_id}
              resultSetSelected={Boolean(foundResult)}
            />
          )}
        </Await>
      </Suspense>
    </List>
  );
}

type ProductResultsProps = {
  product: Products;
  resultSets: ResultSetSelectProps["resultSetList"];
  onSelect: ResultSetSelectProps["onSelect"];
  selectedResultSetId: ResultSetSelectProps["selectedResultSetId"];
  onDownloadResult: () => void;
  onCreateTaskMap: () => void;
  drivingDays?: DrivingDay[];
};

function ProductResults({
  product,
  resultSets,
  onSelect,
  selectedResultSetId,
  onDownloadResult,
  onCreateTaskMap,
  drivingDays,
}: ProductResultsProps): React.ReactNode {
  const { t } = useTranslation();
  const isAllowedAgromanager = useIsAllowedAgromanagerUser();

  const navigate = useNavigate();

  const { customerId, fieldId } = useParams();

  const selectedResultSet = resultSets.find(
    (rs) => rs.id === selectedResultSetId,
  );

  if (!resultSets.length) {
    return (
      <div className={styles.listItemBox}>
        <p className={styles.listItemText}>
          {t("message.There is no {{product}} for this field yet", {
            replace: {
              product: translateProductName(t, product).toLowerCase(),
            },
          })}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.listItemBox}>
      <div className="vspace">
        <Select
          key={selectedResultSetId}
          fullWidth
          value={selectedResultSetId}
          onChange={(event) => {
            onSelect(event.target.value);
          }}
          data-testid="ResultSetSelect"
        >
          {resultSets.map((resultSet) => {
            const drivingDay = resultSet.driving_day_id
              ? drivingDays?.find((day) => day.id === resultSet.driving_day_id)
              : undefined;

            const isMultiDay = !!drivingDay && !isSingleDayDrive(drivingDay);
            return (
              <MenuItem value={resultSet.id} key={resultSet.id}>
                {isMultiDay ? (
                  <>
                    <DisplayDate date={drivingDay.day_start_time} />
                    {" – "}
                    <DisplayDate date={drivingDay.day_end_time} />
                  </>
                ) : (
                  <DisplayDate date={resultSet.detection_dates[0]} />
                )}
                {product === Products.TS_APPLE &&
                  (resultSet.calibration ? (
                    <CheckCircleOutlined
                      className={styles.calibratedIcon}
                      fontSize="medium"
                      titleAccess={t("label.calibrated_from_manual_counts")}
                    />
                  ) : (
                    <ReportProblemOutlined
                      className={styles.uncalibratedIcon}
                      fontSize="medium"
                      titleAccess={t("label.uncalibrated_map")}
                    />
                  ))}
                {!resultSet.external_visibility.length && (
                  <VisibilityOff
                    className={styles.listItemIcon}
                    fontSize="small"
                    titleAccess="Result only visible for Aurea users"
                  />
                )}
              </MenuItem>
            );
          })}
        </Select>
      </div>
      {product === Products.TS_APPLE &&
        (selectedResultSet?.calibration ? (
          <div className={styles.iconLegend}>
            <span className={styles.iconLegendItem}>
              <CheckCircleOutlined
                className={styles.calibratedIcon}
                fontSize="small"
              />
              <span>{t("label.calibrated_from_manual_counts")}</span>
            </span>
          </div>
        ) : (
          <div className={styles.iconLegend}>
            <span className={styles.iconLegendItem}>
              <ReportProblemOutlined
                className={styles.uncalibratedIcon}
                fontSize="small"
              />
              <span>{t("label.uncalibrated_map")}</span>
            </span>
          </div>
        ))}
      {product === Products.TS_APPLE &&
        hasDisplacedCalibration(resultSets, selectedResultSet) && (
          <div className="vspace">
            <MessageFlash
              level={MessageLevel.Info}
              message={t("message.calibration_not_applied_to_new_drive")}
            />
          </div>
        )}
      <div className="vspace">
        {product != "TS-APPLE" && (
          <Button
            variant="contained"
            disableElevation
            fullWidth
            size="large"
            onClick={() => onCreateTaskMap()}
          >
            {t("button.Create taskmap")}
          </Button>
        )}
      </div>
      {product == "TS-APPLE" && (
        <div className="vspace">
          <Button
            className={styles.resultListButton}
            variant="contained"
            disableElevation
            fullWidth
            size="large"
            onClick={() =>
              navigate(
                `/customer/${customerId}/field/${fieldId}/result-set/${selectedResultSetId}/calibrate`,
              )
            }
          >
            {selectedResultSet?.calibration
              ? t("button.recalibrate_map")
              : t("button.Calibrate map")}
          </Button>
        </div>
      )}
      <div className="vspace">
        <Button
          variant="outlined"
          disableElevation
          fullWidth
          size="large"
          onClick={() => onDownloadResult()}
        >
          {t("button.Download results")}
        </Button>
      </div>
      {selectedResultSetId &&
        (isAllowedAgromanager ? (
          <div className="vspace">
            <ExportToAgroBtn resultSetId={selectedResultSetId} />
          </div>
        ) : null)}
    </div>
  );
}
